import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile, changePassword } from '../lib/api';
import AccountSidebar from '../components/AccountSidebar';
import '../pages/OrdersPage.css';
import './AccountDetailsPage.css';

export default function AccountDetailsPage({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  React.useEffect(() => {
    if (!user) navigate('/account');
  }, [user, navigate]);

  if (!user) return null;

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileErr(''); setProfileMsg('');
    if (!profile.firstName.trim() || !profile.lastName.trim()) {
      setProfileErr('First and last name are required.');
      return;
    }
    setProfileSaving(true);
    try {
      const updated = await updateProfile(profile);
      if (onUserUpdate) onUserUpdate(updated);
      setProfileMsg('Profile updated successfully.');
    } catch (err) {
      setProfileErr(err.message || 'Could not update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePwSave = async (e) => {
    e.preventDefault();
    setPwErr(''); setPwMsg('');
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirm) {
      setPwErr('Please fill in all password fields.'); return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwErr('New password must be at least 8 characters.'); return;
    }
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwErr('Passwords do not match.'); return;
    }
    setPwSaving(true);
    try {
      await changePassword(pwForm.currentPassword, pwForm.newPassword);
      setPwMsg('Password changed successfully.');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setPwErr(err.message || 'Could not change password.');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="ord-page">
      <AccountSidebar user={user} onLogout={onLogout} active="details" />

      <main className="ord-main acd-main">
        <div className="ord-header">
          <div>
            <h1 className="ord-title">Account details</h1>
            <p className="ord-subtitle">Manage your personal information and password.</p>
          </div>
        </div>

        <section className="acd-card">
          <h2 className="acd-card__title">Personal information</h2>
          <form className="acd-form" onSubmit={handleProfileSave}>
            {profileErr && <div className="acd-alert acd-alert--err">{profileErr}</div>}
            {profileMsg && <div className="acd-alert acd-alert--ok">{profileMsg}</div>}

            <div className="acd-row">
              <label className="acd-label">
                First name
                <input
                  className="acd-input"
                  value={profile.firstName}
                  onChange={e => setProfile(f => ({ ...f, firstName: e.target.value }))}
                />
              </label>
              <label className="acd-label">
                Last name
                <input
                  className="acd-input"
                  value={profile.lastName}
                  onChange={e => setProfile(f => ({ ...f, lastName: e.target.value }))}
                />
              </label>
            </div>

            <label className="acd-label">
              Email address
              <input className="acd-input" value={user.email || ''} disabled />
              <span className="acd-hint">Email can't be changed.</span>
            </label>

            <button className="acd-btn acd-btn--primary" type="submit" disabled={profileSaving}>
              {profileSaving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </section>

        <section className="acd-card">
          <h2 className="acd-card__title">Change password</h2>
          <form className="acd-form" onSubmit={handlePwSave}>
            {pwErr && <div className="acd-alert acd-alert--err">{pwErr}</div>}
            {pwMsg && <div className="acd-alert acd-alert--ok">{pwMsg}</div>}

            <label className="acd-label">
              Current password
              <input
                className="acd-input" type="password"
                value={pwForm.currentPassword}
                onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))}
              />
            </label>
            <div className="acd-row">
              <label className="acd-label">
                New password
                <input
                  className="acd-input" type="password"
                  value={pwForm.newPassword}
                  onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))}
                />
              </label>
              <label className="acd-label">
                Confirm new password
                <input
                  className="acd-input" type="password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                />
              </label>
            </div>

            <button className="acd-btn acd-btn--primary" type="submit" disabled={pwSaving}>
              {pwSaving ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}